/**
 * PlateauInteractive — Les 6 manœuvres officielles plateau A1/A2.
 *
 * Extrait de `pages/Home.jsx` (juin 2026) pour réduire la taille du fichier
 * principal (~226 lignes).
 *
 * Différenciation forte : présentation cinéma des 6 manœuvres avec :
 *  • Numéro éditorial XXL (01 → 06) en accent magazine
 *  • Pictogramme central par manœuvre
 *  • Critère clé officiel (vitesse, durée, pied au sol…)
 *  • Rail horizontal swipeable sur mobile, grid sur desktop
 *  • CTA → /epreuves/plateau (page complète)
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, PlayCircle, Map,
  Move, Snail, Octagon, Users, Waves, ShieldAlert,
} from 'lucide-react';

const MANOEUVRES = [
  {
    num: '01',
    title: 'Déplacer la moto',
    meta: 'Sans le moteur',
    detail: 'Pousser · reculer · béquiller',
    icon: Move,
    accent: '#7dd3fc',
  },
  {
    num: '02',
    title: 'Allure réduite',
    meta: 'Sans passager',
    detail: 'Sans pied au sol · sans arrêt',
    icon: Snail,
    accent: '#86efac',
  },
  {
    num: '03',
    title: "Freinage d'urgence",
    meta: '50 km/h min · radar C6',
    detail: 'Arrêt avant C5 (sec) ou C4 (mouillé)',
    icon: Octagon,
    accent: '#fde68a',
  },
  {
    num: '04',
    title: 'Avec passager',
    meta: 'Allure réduite à 2',
    detail: 'Reprise · parcours · dépose',
    icon: Users,
    accent: '#3b82f6',
  },
  {
    num: '05',
    title: 'Slalom',
    meta: '40 km/h min · ligne C7',
    detail: 'Demi-tour franc en bout de piste',
    icon: Waves,
    accent: '#f97316',
  },
  {
    num: '06',
    title: 'Évitement',
    meta: '50 km/h min · ligne C6',
    detail: 'Aucun cône touché · arrêt en zone',
    icon: ShieldAlert,
    accent: '#dc2626',
  },
];

export default function PlateauInteractive() {
  const navigate = useNavigate();

  return (
    <section
      className="relative overflow-hidden rounded-2xl"
      data-testid="home-plateau-interactive"
      aria-labelledby="plateau-title"
      data-reveal=""
      style={{
        background:
          'radial-gradient(ellipse 90% 70% at 50% 0%, rgba(207, 32, 49, 0.08), transparent 70%), hsl(var(--card))',
      }}
    >
      {/* Pattern en fond très subtil (lignes obliques) */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(45deg, transparent 0 12px, hsl(var(--foreground)) 12px 13px)',
        }}
      />

      <div className="relative px-5 sm:px-8 lg:px-10 py-8 sm:py-10 lg:py-12">
        {/* Header */}
        <div className="flex items-end justify-between mb-6 sm:mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))]" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                Plateau interactif · Officiel 2026
              </span>
            </div>
            <h2
              id="plateau-title"
              className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-foreground"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Les 6 manœuvres en un coup d’œil
            </h2>
            <p className="text-xs sm:text-sm text-foreground/60 mt-2 max-w-xl hidden sm:block">
              Le parcours unique imposé sur tous les centres d’examen
              français. 10 minutes pour démontrer ta maîtrise.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/epreuves/plateau')}
            className="text-[11px] sm:text-xs font-bold text-foreground/70 hover:text-[hsl(var(--primary))] transition-colors inline-flex items-center gap-1 hide-in-oral whitespace-nowrap"
            data-testid="plateau-see-all"
          >
            Parcours complet <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Rail horizontal des 6 manœuvres */}
        <div
          className="flex gap-3 sm:gap-4 overflow-x-auto pb-3 -mx-5 sm:-mx-8 lg:-mx-10 px-5 sm:px-8 lg:px-10 hide-scrollbar"
          style={{
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
          }}
          data-testid="plateau-rail"
        >
          {MANOEUVRES.map((m) => (
            <button
              key={m.num}
              type="button"
              onClick={() => navigate('/epreuves/plateau')}
              data-testid={`plateau-card-${m.num}`}
              className="group relative flex-shrink-0 overflow-hidden rounded-xl bg-background border border-border/50 hover:border-[hsl(var(--primary))]/40 transition-all duration-300 hover:scale-[1.02] active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]"
              style={{
                width: 'clamp(190px, 60vw, 240px)',
                aspectRatio: '4 / 5',
                scrollSnapAlign: 'start',
                boxShadow: '0 4px 16px -10px rgba(0,0,0,0.4)',
              }}
            >
              {/* Filet d'accent en haut */}
              <span
                className="absolute top-0 inset-x-0 h-[3px]"
                style={{ background: m.accent }}
              />

              {/* Numéro géant XXL en arrière-plan, opacité faible */}
              <span
                aria-hidden="true"
                className="absolute right-3 top-2 text-[6.5rem] sm:text-[7.5rem] leading-none font-black opacity-[0.10] tracking-tighter pointer-events-none transition-opacity duration-500 group-hover:opacity-[0.16]"
                style={{
                  color: m.accent,
                  fontFamily: "'Playfair Display', Georgia, serif",
                }}
              >
                {m.num}
              </span>

              {/* Contenu — pictogramme + texte */}
              <div className="relative h-full flex flex-col justify-between p-4 sm:p-5">
                {/* Pictogramme top */}
                <div
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                  style={{
                    background: `${m.accent}1a`,
                    color: m.accent,
                  }}
                >
                  <m.icon className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2} />
                </div>

                {/* Texte bas */}
                <div className="text-left">
                  <span
                    className="block text-[9px] sm:text-[10px] font-black uppercase tracking-[0.18em] mb-1.5"
                    style={{ color: m.accent }}
                  >
                    Manœuvre {m.num}
                  </span>
                  <h3
                    className="text-base sm:text-lg font-extrabold tracking-tight text-foreground leading-tight mb-1"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    {m.title}
                  </h3>
                  <p className="text-[11px] sm:text-xs text-foreground/65 font-medium leading-snug">
                    {m.meta}
                  </p>
                  <p className="text-[10px] sm:text-[11px] text-foreground/45 mt-1 leading-snug line-clamp-2">
                    {m.detail}
                  </p>
                </div>
              </div>
            </button>
          ))}

          {/* Padding fantôme pour le snap mobile */}
          <div className="flex-shrink-0 w-2 sm:hidden" aria-hidden="true" />
        </div>

        {/* CTA bas — invitation à voir le parcours complet */}
        <div className="flex items-center justify-between mt-7 sm:mt-9 pt-6 border-t border-border/40">
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex w-10 h-10 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] items-center justify-center flex-shrink-0">
              <Map className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm sm:text-base font-bold text-foreground leading-tight">
                Schéma officiel · Vidéos tutos · Notation A/B/C
              </p>
              <p className="text-[11px] sm:text-xs text-foreground/55 mt-0.5">
                Tout le parcours plateau, gratuit et sans inscription.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/epreuves/plateau')}
            className="editorial-btn-primary hero-cta-glow flex-shrink-0 hide-in-oral"
            data-testid="plateau-cta"
            style={{
              background: 'hsl(var(--primary))',
              borderColor: 'hsl(var(--primary))',
              color: '#fff',
            }}
          >
            <PlayCircle className="w-4 h-4" />
            <span className="hidden sm:inline">Découvrir le parcours</span>
            <span className="sm:hidden">Voir tout</span>
          </button>
        </div>
      </div>
    </section>
  );
}
